import React, { useEffect, useMemo, useState } from 'react';
import scssVariables from '../../../../../Config.module.scss';
import { Currency, MarketIndicatorFactorDataModel, UnitOfMeasure } from '../../../../../Generated/Raven-Demeter';
import {
    DecileParameters,
    leadingIndicatorTypeDefinitions,
} from '../../../../Pages/Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { chartColors, IChartAreaRangeDataSeries, IChartDataSeries } from '../../ChartDefinitions';
import chartService from '../../ChartService';
import ChartWrapper from '../../ChartWrapper/ChartWrapper';
import { defaultDecileParameters } from '../MarketIndicatorChartDefaultParameters';
import marketIndicatorChartService from '../MarketIndicatorChartService';
import { defaultChartPalette, IMarketIndicatorChartColorPalette, IMarketIndicatorUserChartBaseProps } from '../MarketIndicatorUserChartDefinitions';
import MarketIndicatorUserChartRaw from '../MarketIndicatorUserChartRaw';
import styles from './MarketIndicatorDecileChart.module.scss';
import marketIndicatorDecileChartService from './MarketIndicatorDecileChartService';

export const decileChartPalette: IMarketIndicatorChartColorPalette = {
    dataDecileAlphaColorsRuleSetTwo: defaultChartPalette.priceDecileAlphaColorsRuleSetTwo,
    lineChartColorsRuleSetTwo: [scssVariables.ruleSetTwoLineColor1, scssVariables.ruleSetTwoLineColor2, scssVariables.ruleSetTwoLineColor3],
};

export interface IMarketIndicatorDecileChartProps extends IMarketIndicatorUserChartBaseProps {
    parameters?: DecileParameters;
}

const MarketIndicatorDecileChart: React.FC<IMarketIndicatorDecileChartProps> = (props: IMarketIndicatorDecileChartProps) => {
    const [translations, translate] = useLanguage();
    const [lineSeries, setLineSeries] = useState<IChartDataSeries[]>([]);
    const [areaRangeSeries, setAreaRangeSeries] = useState<IChartAreaRangeDataSeries[]>([]);

    const yearsOfData = useMemo(() => {
        const parameters = props.parameters ?? defaultDecileParameters;
        const value = Number(parameters.yearsOfData);
        return value;
    }, [props.parameters]);

    const oneYearWeightedPercent = useMemo(() => {
        const parameters = props.parameters ?? defaultDecileParameters;
        const value = Number(parameters.oneYearWeightedPercent);
        if (Number.isFinite(value)) {
            return value;
        }
        return undefined;
    }, [props.parameters]);

    useEffect(() => {
        const calculationData = marketIndicatorChartService.filterByYears(chartData, yearsOfData + 1);
        const thisYearData = marketIndicatorChartService.filterByYears(calculationData, 1);
        const decilesData = marketIndicatorDecileChartService.calculateDecilesData(calculationData, yearsOfData, oneYearWeightedPercent);
        const newLinesSeries: IChartDataSeries[] = [
            {
                label: translations.text.thisYear,
                data: thisYearData,
                axisLabel: props.yAxisLabel,
            },
        ];

        const newAreaRangeSeries: IChartAreaRangeDataSeries[] = [
            {
                label: `90% - ${translations.words.maximum}`,
                data: marketIndicatorChartService.filterByYears(decilesData[10], 1),
                decileRank: 10,
            },
            {
                label: '80 - 90%',
                data: marketIndicatorChartService.filterByYears(decilesData[9], 1),
                decileRank: 9,
            },
            {
                label: '70 - 80%',
                data: marketIndicatorChartService.filterByYears(decilesData[8], 1),
                decileRank: 8,
            },
            {
                label: '60 - 70%',
                data: marketIndicatorChartService.filterByYears(decilesData[7], 1),
                decileRank: 7,
            },
            {
                label: '50 - 60%',
                data: marketIndicatorChartService.filterByYears(decilesData[6], 1),
                decileRank: 6,
            },
            {
                label: '40 - 50%',
                data: marketIndicatorChartService.filterByYears(decilesData[5], 1),
                decileRank: 5,
            },
            {
                label: '30 - 40%',
                data: marketIndicatorChartService.filterByYears(decilesData[4], 1),
                decileRank: 4,
            },
            {
                label: '20 - 30%',
                data: marketIndicatorChartService.filterByYears(decilesData[3], 1),
                decileRank: 3,
            },
            {
                label: '10 - 20%',
                data: marketIndicatorChartService.filterByYears(decilesData[2], 1),
                decileRank: 2,
            },
            {
                label: `${translations.words.minimum} - 10%`,
                data: marketIndicatorChartService.filterByYears(decilesData[1], 1),
                decileRank: 1,
            },
        ];
        setLineSeries(newLinesSeries);
        setAreaRangeSeries(newAreaRangeSeries);
    }, [props.runTestMarketIndicatorFactorResponse, yearsOfData, oneYearWeightedPercent]);

    const chartData = useMemo(() => {
        if (
            props.runTestMarketIndicatorFactorResponse &&
            props.runTestMarketIndicatorFactorResponse.rows &&
            props.runTestMarketIndicatorFactorResponse.rows.length > 0
        ) {
            return marketIndicatorChartService.convertToChartData((props.runTestMarketIndicatorFactorResponse?.rows as MarketIndicatorFactorDataModel[]) ?? []);
        }
        return [];
    }, [props.runTestMarketIndicatorFactorResponse]);

    const isLoading = useMemo(
        () => props.isLoading || !props.runTestMarketIndicatorFactorResponse,
        [props.runTestMarketIndicatorFactorResponse, props.isLoading],
    );

    const title = useMemo(() => {
        if (props.title) {
            return props.title;
        }
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.commodityDisplayName) {
            const region = props.runTestMarketIndicatorFactorResponse?.subRegion
                ? translate(props.runTestMarketIndicatorFactorResponse?.subRegion)
                : translate(props.runTestMarketIndicatorFactorResponse?.regionDisplayName!);
            const tableDefinitionType =
                translations.tableDefinitionType[
                    leadingIndicatorTypeDefinitions.find((x) => x.value === props.runTestMarketIndicatorFactorResponse?.leadingIndicatorType)
                        ?.tableDefinitionType!
                ];
            const commdityName = props.runTestMarketIndicatorFactorResponse.commodityDisplayName;
            return [region, commdityName, tableDefinitionType].filter((x) => x !== null && x !== '').join(' ');
        }
        return '';
    }, [props.title, props.runTestMarketIndicatorFactorResponse]);

    const sourceTag = useMemo(() => {
        if (
            props.runTestMarketIndicatorFactorResponse &&
            props.runTestMarketIndicatorFactorResponse.dataSourceTags &&
            props.runTestMarketIndicatorFactorResponse.dataSourceTags.length > 0
        ) {
            return `${props.runTestMarketIndicatorFactorResponse.dataSourceTags[0]}, ${translations.dataSource.StoneXCalculations}`;
        }
        return '';
    }, [props.runTestMarketIndicatorFactorResponse]);

    const currency = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.currency) {
            return props.runTestMarketIndicatorFactorResponse.currency;
        }
        return '' as Currency;
    }, [props.runTestMarketIndicatorFactorResponse]);

    const unitOfMeasure = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.unitOfMeasure) {
            return props.runTestMarketIndicatorFactorResponse.unitOfMeasure;
        }
        return '' as UnitOfMeasure;
    }, [props.runTestMarketIndicatorFactorResponse]);

    const displayUnitOfMeasure = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.dataCombinationDisplayUnits) {
            return props.runTestMarketIndicatorFactorResponse.dataCombinationDisplayUnits;
        }

        return '' as UnitOfMeasure;
    }, [props.runTestMarketIndicatorFactorResponse]);

    const rightSide = useMemo(
        () => (
            <div className={styles.historical_market_indicator_chart_secondary_legend}>
                {areaRangeSeries.map((x) => {
                    const currentColor = chartColors.priceDecileAlphaColorsRuleSetTwo.find((y) => y.decileRank === x.decileRank)?.color;
                    return (
                        <div key={`areaRangeSeries_${x.label}`} className={styles.historical_market_indicator_chart_right_side}>
                            <div className={styles.historical_market_indicator_chart_right_side_label} style={{ backgroundColor: currentColor }} />
                            {x.label}
                        </div>
                    );
                })}
            </div>
        ),
        [areaRangeSeries],
    );

    return (
        <ChartWrapper
            name="MarketIndicatorDecileChart"
            title={title}
            customTitle={props.customTitle}
            marketIndicatorOutlook={props.marketIndicatorOutlook}
            dataSourceTag={sourceTag}
            isLoading={isLoading}
            headerOptions={{
                showOnlyAsPopout: props.showOnlyAsPopout,
            }}
            showPopout={props.showPopout}
            setShowPopout={props.setShowPopout}
            rightSide={rightSide}
            testId="MarketIndicatorFactorChart"
            track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorDecileChart' }}
        >
            <MarketIndicatorUserChartRaw
                lineSeries={lineSeries}
                areaRangeSeries={areaRangeSeries}
                currencies={[currency]}
                unitOfMeasures={[unitOfMeasure]}
                displayUnitOfMeasure={displayUnitOfMeasure}
                focusOnLine
                hideAreaRangeSeriesInLegend
                colorPalette={decileChartPalette}
                disableTooltip={chartService.isRestrictedDataSource(...(props.runTestMarketIndicatorFactorResponse?.dataSourceTags ?? []))}
            />
        </ChartWrapper>
    );
};

export default MarketIndicatorDecileChart;
